import { type ClipboardEvent, type KeyboardEvent, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import RichText from "common/blocks/RichText";
import TextInput from "common/blocks/TextInput";
import useModalContext from "common/providers/modal/useModalContext";
import useKeysValidation from "hooks/useKeysValidation/useKeysValidation";
import { ALPHADIGITS_REGEX } from "lib/constants";
import { DeliveryOrCollectionFormSchema } from "lib/schemas/schemas";
import { useForm } from "react-hook-form";
import { logError } from "utils/logger";
import { z } from "zod";

import { deliveryOrCollectionDetailedModal } from "../..";
import type { CollectionTransferDeliveryWidgetProps } from "../../../components/WidgetsRow/components/CollectionTransferDeliveryWidget";
import TransferDeliveryRow from "../../../components/WidgetsRow/components/CollectionTransferDeliveryWidget/TransferDeliveryRow";
import useClosestStoresContext from "../../../providers/closestStores/useClosestStoresContext";
import Wrapper from "../../common/Wrapper";
import styles from "../DeliveryOrCollection.module.scss";

const cx = classNames.bind(styles);

type DeliveryOrCollectionFormInputs = z.infer<typeof DeliveryOrCollectionFormSchema>;

const DeliveryOrCollectionFormView = ({
    params,
}: {
    params: CollectionTransferDeliveryWidgetProps;
}) => {
    const {
        afterPhoneNumberText,
        freeText,
        homeDeliveryAvailableText,
        readyForCollectionText,
        readyForCollectionDateTime,
        storeDirectionUrl,
        enterPostcodeFreeText,
        privacyPolicyInfo,
        submitPostcodeButtonText,
        getDirectionButtonText,
        homeDeliveryPrice,
        showHomeDelivery,
    } = params;
    const [loading, setLoading] = useState(false);

    const {
        getClosestStores,
        hasClosestStores,
        lastPostcode,
        reset: resetClosestStores,
    } = useClosestStoresContext();

    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();

    const { handleKeyDownEvent, handlePasteEvent } = useKeysValidation();

    const { register, watch, setValue, handleSubmit, reset, formState } = useForm({
        defaultValues: {
            postCode: lastPostcode,
        },
        resolver: zodResolver(DeliveryOrCollectionFormSchema),
    });

    const onSubmit = async (data: DeliveryOrCollectionFormInputs) => {
        setLoading(true);
        try {
            await getClosestStores(data);
        } catch (error: unknown) {
            const err = error as Error;
            logError(err.message, err);
        }
        setLoading(false);
        openSidebar(deliveryOrCollectionDetailedModal, params);
    };

    return (
        <Wrapper className={cx("wrapper")}>
            <div className={cx("deliveryOrCollectionSidebar")}>
                <TransferDeliveryRow
                    key="readyForCollection"
                    icon="PropertyLocation"
                    price={freeText}
                    className={cx({ hasClosestStores }, "rowPadding")}
                >
                    <RichText {...readyForCollectionText} />
                    <span className={cx("fromDate")}>{readyForCollectionDateTime}</span>
                    <div className={cx("buttonRow")}>
                        <Button
                            type="button"
                            theme="textLink"
                            href={storeDirectionUrl}
                            target="_blank"
                            ariaLabel={getDirectionButtonText}
                        >
                            {getDirectionButtonText}
                        </Button>
                    </div>
                </TransferDeliveryRow>
                <form className={cx("form", "rowPadding")} onSubmit={handleSubmit(onSubmit)}>
                    <RichText className={cx("richTextNoMargin")} {...enterPostcodeFreeText} />

                    <div className={cx("controls")}>
                        <TextInput
                            label="Your postcode"
                            maxLength={10}
                            placeholder="Your postcode"
                            value={watch("postCode")}
                            hasError={!!formState.errors?.postCode?.message}
                            errorText={formState.errors?.postCode?.message}
                            {...register("postCode", {
                                onChange: (event) => {
                                    setValue("postCode", event.target.value.toUpperCase());
                                },
                            })}
                            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                                handleKeyDownEvent(event, ALPHADIGITS_REGEX)
                            }
                            onPaste={(event: ClipboardEvent<HTMLInputElement>) =>
                                handlePasteEvent(event, ALPHADIGITS_REGEX)
                            }
                            showResetButton={hasClosestStores}
                            onReset={() => {
                                resetClosestStores();
                                reset({ postCode: "" });
                            }}
                        />
                        <Button
                            fullWidth
                            className={cx("primaryFont")}
                            type="submit"
                            ariaLabel={submitPostcodeButtonText}
                            loading={loading}
                        >
                            {submitPostcodeButtonText}
                        </Button>
                    </div>

                    <RichText
                        className={cx("richTextNoMargin", "richTextPrivacyInfo")}
                        {...privacyPolicyInfo}
                    />
                </form>
                {showHomeDelivery && (
                    <TransferDeliveryRow
                        key="home-delivery"
                        icon="DeliveryVan"
                        price={homeDeliveryPrice}
                        className={cx("rowPadding")}
                    >
                        <p>
                            {homeDeliveryAvailableText}.
                            <br />
                            Call <DynamicPhoneNumber isLink className={cx("phoneNumber")} />{" "}
                            {afterPhoneNumberText}
                        </p>
                    </TransferDeliveryRow>
                )}
            </div>
        </Wrapper>
    );
};

export default DeliveryOrCollectionFormView;
